import {FC, useEffect, useMemo, useRef, useState} from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  MenuProps,
  Select,
  Typography,
  useTheme
} from '@mui/material';
import * as sentry from '@sentry/react';
import {Form, Formik, FormikErrors, FormikHelpers, FormikTouched} from 'formik';
import {useTranslation} from 'react-i18next';
import {When} from 'react-if';

import editIconImage from 'assets/images/edit-pencil.svg';
import Button from 'components/button/Button';
import Input from 'components/input/Input';
import {ThaiPhoneNumber} from 'components/thai-phone-number/ThaiPhoneNumber';
import {config} from 'config/env';
import {DEFAULT_LANGUAGE, SubDistrictStatusType} from 'constants/index';
import useAnalytics, {SUGGESTEDLIST_CHANNELS, EVENT_SOURCE} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import useDebounce from 'hooks/useDebounce';
import {getStoreAreasV2} from 'services/location/location-service';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {CurrentStoreType} from 'store/types/app';
import {CreateAddressInputType, IUserDetail, StoreAreas, UserAddress} from 'types';
import {
  assignDefaults,
  filterStoreArea,
  findAvailableIds,
  getAddressString,
  getAllKysInFilterArea,
  projectFilteredArea,
  setDefaults,
  trimValues
} from 'utils';
import {useAddressFormValidationSchema} from 'utils/addressFormSchema';
import {filter1PItems, filter3PItems, getValue} from 'utils/helper';
import {phoneDisplayFormat} from 'utils/phoneNumber';

import {AddressFullMapView, StyledMapWrapperContainer, StyledTextOverLayContainer} from './AddressFullMapView';
import {StaticMap} from './StaticMap';
import {StyledFormControl} from './styles';
import {ContactInformationModal} from '../basket-modals/ContactInformationModal';

type AddressFormProps = {
  address?: UserAddress;
  handleSubmit: (
    values: CreateAddressInputType,
    formikHelpers: FormikHelpers<CreateAddressInputType>
  ) => void | Promise<any>;
  canUseFullMapView: boolean;
  loading: boolean;
  setFullMapView?: (arg: boolean) => void;
  fullMapView?: boolean;
  userAddressList?: any;
  isUserEnabledEtax?: boolean;
  currentStore?: CurrentStoreType;
  locationsV2?: StoreAreas[];
  setOpenFullMapView?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const AddressForm: FC<AddressFormProps> = ({
  address,
  handleSubmit,
  canUseFullMapView,
  loading,
  setFullMapView,
  fullMapView,
  userAddressList,
  isUserEnabledEtax = false,
  currentStore,
  locationsV2,
  setOpenFullMapView,
  ...rest
}) => {
  const theme = useTheme();
  const analytics = useAnalytics();
  const {t, i18n} = useTranslation();
  const isThai = i18n.language === DEFAULT_LANGUAGE;
  const {isExpress} = useAppMode();

  const [allStoreArea, setAllStoreArea] = useState([] as StoreAreas[]);
  const [filteredStoreArea, setFilteredStoreArea] = useState([] as StoreAreas[]);
  const [isLoading, setIsLoading] = useState(true);

  const {userDetails} = useAppSelector((state) => state?.auth) || {};
  const basket = useAppSelector((state) => state?.basket?.basket);

  const validationSchema = useAddressFormValidationSchema(filteredStoreArea);

  const formikref = useRef(undefined) as any;

  const autoFilled = useRef(false);

  const isPinMapV2Enabled = config.IS_PIN_MAP_V2_ENABLED;

  const [initialValues, setInitialValues] = useState<CreateAddressInputType>({
    id: address?.id,
    address1: address?.address1 || '',
    address2: address?.address2 || '',
    company: address?.company || '',
    country: address?.country || 'Thailand',
    firstName: address?.firstName || '',
    lastName: address?.lastName || '',
    mobile: phoneDisplayFormat(address?.mobile || '') || '',
    province_id: address?.province?.id || '',
    district_id: address?.district?.id || '',
    subDistrict_id: address?.subDistrict?.id || '',
    zip: address?.zip || '',
    longitude: address?.longitude || 0,
    latitude: address?.latitude || 0,
    user_longitude: address?.user_longitude || 0,
    user_latitude: address?.user_latitude || 0,
    deliveryInstructions: address?.deliveryInstructions || '',
    isDefault: userAddressList?.length === 0 ? true : address?.isDefault || false,
    branchId: address?.branchId || '',
    taxId: address?.taxId || '',
    email: address?.email || '',
    metadata: address?.metadata || {}
  });

  const [mapString, setMapString] = useState<string | undefined>(undefined);

  const [isContactFormVisible, setIsContactFormVisible] = useState(false);

  const isAddressSectionsEnabled = config.IS_ADDRESS_SECTIONS_ENABLED;
  const isReactNativeView = ReactNative.isReactNativeWebView();

  useEffect(() => {
    if (formikref?.current) {
      formikref?.current?.validateForm();
    }
  }, [filteredStoreArea]);

  const handleZipChange = useDebounce({
    fn: async (z, setFieldValue) => {
      if (z.length === 5) {
        try {
          setIsLoading(true);
          const storeAreas = await getStoreAreasV2(z as string);
          const availableIds = findAvailableIds({
            data: storeAreas,
            z: getAllKysInFilterArea(storeAreas, 'z'),
            pIDs: getAllKysInFilterArea(storeAreas, 'pID'),
            dIDs: getAllKysInFilterArea(storeAreas, 'dID'),
            sIDs: getAllKysInFilterArea(storeAreas, 'sID')
          });
          setDefaults(availableIds, setFieldValue);
          setFilteredStoreArea(storeAreas);
        } catch (e) {
          console.error('Error during zipCode change:', e);
          sentry.captureException(e);
        } finally {
          setTimeout(() => setIsLoading(false), 0);
        }
      }
    },
    delay_ms: 1000
  });

  useEffect(() => {
    //? this is to auto fill zip and district upon adding new address or when location from pinmap view is changed
    if (formikref?.current?.setFieldValue && !autoFilled.current && !address?.province) {
      if (config.IS_GOOGLE_ZIP_ENABLED && initialValues.zip) {
        if (config.IS_LOCATION_SVC_ENABLED && filteredStoreArea.length) {
          autoFilled.current = true;
        } else if (initialValues.zip && allStoreArea.length) {
          const data = filterStoreArea(allStoreArea, {z: initialValues.zip});
          const availableIds = findAvailableIds(data);
          setDefaults(availableIds, formikref.current.setFieldValue);
          setFilteredStoreArea(data.data);
          autoFilled.current = true;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStoreArea.length, filteredStoreArea.length]);

  const isMapStringUpdated = (values: CreateAddressInputType, touched: FormikTouched<CreateAddressInputType>) => {
    if (!values.user_longitude && !values.longitude) {
      return true;
    }
    if (
      (values.user_longitude || values.longitude) &&
      !touched.address1 &&
      !touched.zip &&
      !touched.province_id &&
      !touched.district_id &&
      !touched.subDistrict_id
    ) {
      return false;
    }
    const generatedString = getAddressStringWrapper(values);
    return !(generatedString === mapString);
  };

  const getAddressStringWrapper = (values: CreateAddressInputType, updateToState = false) => {
    const addressString = getAddressString(
      allStoreArea,
      values.province_id,
      values.district_id,
      values.subDistrict_id,
      values.zip,
      values.address1,
      values.address2
    );
    if (updateToState) {
      setMapString(addressString);
    }
    return addressString;
  };

  useEffect(() => {
    if (initialValues && allStoreArea && allStoreArea.length && canUseFullMapView) {
      getAddressStringWrapper(initialValues, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStoreArea, initialValues, canUseFullMapView]);

  useEffect(() => {
    (async () => {
      const {v, data} = await assignDefaults({
        val: initialValues,
        curr: currentStore,
        isThai,
        locationsV2
      });
      if (!config.IS_LOCATION_SVC_ENABLED) {
        setAllStoreArea(data);
      }

      let userParams = v as Partial<CreateAddressInputType>;
      if (!address?.id && userDetails) {
        if (userDetails) {
          userParams = {
            ...v,
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
            email: userDetails.email,
            mobile: phoneDisplayFormat(userDetails.mobile) || ''
          };
        }
      }

      setInitialValues(userParams as CreateAddressInputType);
      setFilteredStoreArea(filterStoreArea(data, {z: v.zip, pID: v.province_id}).data);
      setTimeout(() => setIsLoading(false), 0);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const textFieldIsMandatory = t('message.fieldIsMandatory');

  const handleSubmitButton = async (
    values: CreateAddressInputType,
    formHelper: FormikHelpers<CreateAddressInputType>
  ) => {
    const province = filteredStoreArea.find(({pID}) => pID === values.province_id);
    const district = filteredStoreArea.find(({dID}) => dID === values.district_id);
    const subDistrict = filteredStoreArea.find(({sID}) => sID === values.subDistrict_id);
    const mobile = values.mobile.replaceAll(' ', '');

    if (values.email) {
      values.email = values.email.trim().toLocaleLowerCase();
    }
    if (values.taxId) {
      values.taxId = values.taxId.trim();
    }

    if (values.branchId) {
      values.branchId = values.branchId?.trim();
    }

    if (!values?.user_latitude || !values?.user_longitude) {
      setFullMapView && setFullMapView(true);
      formHelper.setSubmitting(false);
      SendBrowserEvent.showInfoToast(t('addressV2.confirmLocation'));
      return;
    }

    const trimmedValues = trimValues(values);
    handleSubmit(
      {
        ...trimmedValues,
        mobile,
        provinceName: province?.pe || '',
        districtName: district?.de || '',
        subDistrictName: subDistrict?.se || ''
      },
      formHelper as any
    );
    formHelper.setSubmitting(false);
  };

  const handlePaste = (e: any) => {
    e.preventDefault();
  };

  //todo: remove this
  const handleMapClick = async (
    values: CreateAddressInputType,
    setSubmitting: (isSubmitting: boolean) => void,
    errors: FormikErrors<CreateAddressInputType>
  ) => {
    if (
      !values?.firstName ||
      !values?.lastName ||
      !values?.mobile ||
      !values?.zip ||
      !values?.province_id ||
      !values?.district_id ||
      !values?.subDistrict_id ||
      !values?.address1 ||
      errors?.mobile
    ) {
      return;
    } else {
      canUseFullMapView && !isLoading && setFullMapView && setFullMapView(true);
    }
  };

  const isAllRequiredFieldsNotAdded = (
    values: CreateAddressInputType,
    errors: FormikErrors<CreateAddressInputType>
  ) => {
    if (
      !values?.firstName ||
      !values?.lastName ||
      !values?.mobile ||
      !values?.zip ||
      values?.zip?.length < 5 ||
      !values?.province_id ||
      !values?.district_id ||
      !values?.subDistrict_id ||
      !values?.address1 ||
      errors?.mobile
    ) {
      return true;
    }
    return false;
  };

  const addAddressEvent = (eventName: string, payload: any) => {
    analytics.trackEvent(eventName, {
      zip: currentStore?.zipcode,
      sub: currentStore?.subdistrict || '',
      addressesTotal: userAddressList?.length,
      storeId: basket?.storeCode || '',
      channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
      segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0,
      ...payload
    });
  };

  //todo: remove this
  const MemoizedStaticMap = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
    values: CreateAddressInputType,
    touched: FormikTouched<CreateAddressInputType>
  ) =>
    useMemo(
      () => (
        <StaticMap
          addressString={isMapStringUpdated(values, touched) ? getAddressStringWrapper(values, true) : undefined}
          latLng={
            !isMapStringUpdated(values, touched)
              ? {
                  lat: values?.user_latitude || values?.latitude,
                  lng: values?.user_longitude || values?.longitude
                }
              : undefined
          }
          onAddressChange={() => {
            if (isMapStringUpdated(values, touched)) {
              setFieldValue('user_longitude', null);
              setFieldValue('user_latitude', null);
              setFieldValue('longitude', null);
              setFieldValue('latitude', null);
            }
          }}
          addAddressEvent={addAddressEvent}
        />
      ),
      [values, touched, setFieldValue]
    );

  const _updateContactInformation = (newValues: CreateAddressInputType, newContactInfo: Partial<IUserDetail>) => {
    setInitialValues({
      ...newValues,
      ...newContactInfo
    });
  };

  if (!initialValues.mobile && isAddressSectionsEnabled) {
    return null;
  }

  return (
    <>
      <Formik
        {...rest}
        innerRef={formikref as any}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        validateOnChange
        onSubmit={(values, formHelper) => {
          handleSubmitButton(values, formHelper);
        }}
      >
        {({
          setFieldValue,
          handleChange,
          handleBlur,
          isSubmitting,
          values,
          errors,
          touched,
          setSubmitting,
          setFieldTouched
        }) => (
          <Form name="address-form" noValidate>
            <When condition={!fullMapView}>
              <When condition={isAddressSectionsEnabled && values.firstName}>
                <Grid2 sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <Typography variant="h6" sx={{fontWeight: '600'}}>
                    {t('label.contactInformation')}
                  </Typography>
                  <IconButton onClick={() => setIsContactFormVisible(true)}>
                    <Box component="img" height={16} src={editIconImage}></Box>
                  </IconButton>
                </Grid2>
                <Grid2>
                  <Typography variant="body1" sx={{fontWeight: '400', lineHeight: '2.5'}}>
                    {values.firstName} {values.lastName} ({values.mobile})
                  </Typography>
                  <Typography variant="body1" sx={{fontWeight: '400'}}>
                    {values.email}
                  </Typography>
                </Grid2>
                <Divider sx={{pb: 1}} />
                <Grid2 py={2}>
                  <Typography variant="h6" sx={{fontWeight: '600'}}>
                    {t('label.addressInformation')}
                  </Typography>
                </Grid2>
              </When>
              <Grid container py={isAddressSectionsEnabled ? 0 : 2} spacing={2} alignItems="flex-start">
                <When condition={!isAddressSectionsEnabled}>
                  <Grid item mobile={12} tablet={4}>
                    <Input
                      id="first_name_input"
                      fullWidth
                      label={t('label.firstName')}
                      error={Boolean(getValue(errors, 'firstName', false)) && touched.firstName}
                      name="firstName"
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.firstName}
                      helperText={textFieldIsMandatory}
                      inputProps={{maxLength: 200}}
                    />
                  </Grid>
                  <Grid item mobile={12} tablet={4}>
                    <Input
                      id="last_name_input"
                      fullWidth
                      label={t('label.lastName')}
                      error={Boolean(getValue(errors, 'lastName', false)) && touched.lastName}
                      name="lastName"
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.lastName}
                      helperText={textFieldIsMandatory}
                      inputProps={{maxLength: 100}}
                    />
                  </Grid>
                  <Grid item mobile={12} tablet={4}>
                    <ThaiPhoneNumber
                      id="phone_number_input"
                      label={t('label.phoneNumber')}
                      error={Boolean(getValue(errors, 'mobile', false)) && touched.mobile}
                      type="text"
                      fullWidth
                      required
                      name="mobile"
                      helperText={textFieldIsMandatory}
                      onChange={(e) => {
                        setFieldValue('mobile', e);
                      }}
                      placeholder="xxx xxx xxxx"
                      onBlur={handleBlur}
                      value={values?.mobile}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Input
                      id="email_input"
                      fullWidth
                      label={t('label.emailOptional')}
                      error={Boolean(getValue(errors, 'email', false)) && touched.email}
                      name="email"
                      required={isUserEnabledEtax}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      helperText={getValue(errors, 'email', false)}
                    />
                  </Grid>
                </When>
                <Grid item xs={12}>
                  <Input
                    fullWidth
                    id="address_title_input"
                    label={t('label.address')}
                    error={Boolean(getValue(errors, 'address1', false)) && touched.address1}
                    name="address1"
                    required
                    onChange={(event) => {
                      setFieldTouched('address1');
                      handleChange(event);
                    }}
                    onBlur={handleBlur}
                    value={values.address1}
                    onPaste={handlePaste}
                    helperText={getValue(errors, 'address1', false)}
                  />
                </Grid>
                <Grid item mobile={12}>
                  <Input
                    id="zipcode_input"
                    fullWidth
                    label={t('label.zipCode')}
                    error={Boolean(getValue(errors, 'zip', false)) && touched.zip}
                    name="zip"
                    required
                    onChange={(e) => {
                      const z = e.currentTarget.value;
                      setFieldValue('zip', z);
                      if (config.IS_LOCATION_SVC_ENABLED) {
                        handleZipChange(z, setFieldValue);
                      } else if (z) {
                        const data = filterStoreArea(allStoreArea, {z});
                        const availableIds = findAvailableIds(data);
                        setDefaults(availableIds, setFieldValue);
                        setFilteredStoreArea(data.data);
                      }
                    }}
                    onBlur={handleBlur}
                    value={values.zip}
                    disabled={!!values.zip && isExpress}
                    helperText={getValue(errors, 'zip', false)}
                  />
                </Grid>
                <Grid item mobile={12} tablet={4}>
                  <StyledFormControl variant="filled" fullWidth>
                    <InputLabel required id="province-select-label">
                      {t('label.province')}
                    </InputLabel>
                    <Select
                      disableUnderline
                      labelId="province-select-label"
                      error={Boolean(getValue(errors, 'province_id', false)) && touched.province_id}
                      id="province-select"
                      name="province_id"
                      required
                      defaultValue=""
                      onChange={(e) => {
                        const pID = e.target.value;
                        const data = filterStoreArea(
                          config.IS_LOCATION_SVC_ENABLED ? filteredStoreArea : allStoreArea,
                          {
                            pID
                          }
                        );
                        setFieldValue('zip', data.data[0]?.z);
                        setDefaults(data, setFieldValue);
                        setFilteredStoreArea(data.data);
                      }}
                      onBlur={handleBlur}
                      value={values.province_id}
                      MenuProps={SelectMenuProps}
                      disabled={isLoading || isExpress}
                    >
                      {projectFilteredArea(
                        config.IS_LOCATION_SVC_ENABLED
                          ? filteredStoreArea
                          : filterStoreArea(allStoreArea, {z: values.zip}).data,
                        'pID'
                      ).map((elm) => (
                        <MenuItem key={elm.pID} value={elm.pID}>
                          {isThai ? elm.p : elm.pe}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error={Boolean(getValue(errors, 'province_id'))}>
                      {touched.province_id && getValue(errors, 'province_id')}
                    </FormHelperText>
                  </StyledFormControl>
                </Grid>
                <Grid item mobile={12} tablet={4}>
                  <StyledFormControl variant="filled" fullWidth>
                    <InputLabel required id="district-select-label">
                      {t('label.district')}
                    </InputLabel>
                    <Select
                      disableUnderline
                      labelId="district-select-label"
                      error={Boolean(getValue(errors, 'district_id', false)) && touched.district_id}
                      id="district-select"
                      name="district_id"
                      required
                      defaultValue=""
                      onChange={(e) => {
                        const dID: string = e.target.value;
                        const availableSubDistrict = filteredStoreArea.find(
                          ({dID: _dID, status}) => _dID === dID && status === SubDistrictStatusType.DELIVERABLE
                        );
                        const sIDs = availableSubDistrict ? [availableSubDistrict.sID] : undefined;
                        setDefaults(
                          {
                            pIDs: [values.province_id],
                            dIDs: [dID],
                            sIDs
                          },
                          setFieldValue
                        );
                      }}
                      disabled={isLoading || !values.province_id || isExpress}
                      onBlur={handleBlur}
                      value={values.district_id}
                      MenuProps={SelectMenuProps}
                    >
                      {projectFilteredArea(filteredStoreArea, 'dID').map((elm) => (
                        <MenuItem key={elm.dID} value={elm.dID}>
                          {isThai ? elm.d : elm.de}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error={Boolean(getValue(errors, 'district_id'))}>
                      {touched.district_id && getValue(errors, 'district_id')}
                    </FormHelperText>
                  </StyledFormControl>
                </Grid>
                <Grid item mobile={12} tablet={4}>
                  <StyledFormControl variant="filled" fullWidth>
                    <InputLabel required id="subdistrict-select-label">
                      {t('label.subDistrict')}
                    </InputLabel>
                    <Select
                      disableUnderline
                      labelId="subdistrict-select-label"
                      error={Boolean(getValue(errors, 'subDistrict_id', false)) && touched.subDistrict_id}
                      id="subdistrict-select"
                      name="subDistrict_id"
                      required
                      defaultValue=""
                      onChange={(e) => {
                        handleChange(e);
                        handleBlur(e);
                      }}
                      onBlur={handleBlur}
                      value={values.subDistrict_id}
                      MenuProps={SelectMenuProps}
                      disabled={isLoading || isExpress}
                    >
                      {projectFilteredArea(
                        filterStoreArea(filteredStoreArea, {dID: values.district_id}).data,
                        'sID'
                      )?.map((elm) => (
                        <MenuItem
                          key={elm.sID}
                          value={elm.sID}
                          disabled={elm.status !== SubDistrictStatusType.DELIVERABLE}
                        >
                          {isThai ? elm.s : elm.se}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error={Boolean(getValue(errors, 'subDistrict_id'))}>
                      {touched.subDistrict_id && getValue(errors, 'subDistrict_id')}
                    </FormHelperText>
                  </StyledFormControl>
                </Grid>
                <Grid item xs={12}>
                  <Accordion sx={{boxShadow: 'none'}}>
                    <AccordionSummary sx={{padding: 0}} expandIcon={<ExpandMoreIcon />}>
                      <Grid2 sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography variant="h6" sx={{fontWeight: '600'}}>
                          {t('label.additionalInformation')}
                        </Typography>
                      </Grid2>
                    </AccordionSummary>
                    <AccordionDetails sx={{padding: 0}}>
                      <Grid2 pb={2}>
                        <Input
                          id="apartment_input"
                          fullWidth
                          label={t('label.apartment')}
                          error={Boolean(getValue(errors, 'address2', false)) && touched.address2}
                          name="address2"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.address2}
                          onPaste={handlePaste}
                          helperText={getValue(errors, 'address2', false)}
                        />
                      </Grid2>
                      <Grid2 pb={2}>
                        <Input
                          id="tax_id_input"
                          fullWidth
                          label={isUserEnabledEtax ? t('label.taxId') : t('label.taxIdOptional')}
                          error={Boolean(getValue(errors, 'taxId', false)) && touched.taxId}
                          name="taxId"
                          required={isUserEnabledEtax}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.taxId}
                          helperText={getValue(errors, 'taxId', false)}
                        />
                      </Grid2>

                      <Grid2 pb={2}>
                        <Input
                          id="company_name_input"
                          fullWidth
                          label={t('label.companyOptional')}
                          error={Boolean(getValue(errors, 'company', false)) && touched.company}
                          name="company"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.company}
                        />
                      </Grid2>
                      <Grid2 pb={2}>
                        <Input
                          id="branch_id_input"
                          fullWidth
                          label={t('label.branchIdOptional')}
                          error={Boolean(getValue(errors, 'branchId', false)) && touched.branchId}
                          name="branchId"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.branchId}
                          helperText={getValue(errors, 'branchId', false)}
                        />
                      </Grid2>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                {isPinMapV2Enabled ? null : (
                  <Grid xs={12} item>
                    <When condition={!fullMapView && values.address1 && values.subDistrictName}>
                      <When
                        condition={
                          !isAllRequiredFieldsNotAdded(values, errors) &&
                          !values?.user_latitude &&
                          !values?.user_longitude
                        }
                      >
                        <Box
                          style={{
                            backgroundColor: '#FFEBE5',
                            borderRadius: '10px 10px 0px 0px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#D84018',
                            padding: '8px'
                          }}
                        >
                          <Typography sx={{fontWeight: '600', fontSize: '12px'}}>
                            {t('message.reviewLocation')}
                          </Typography>
                        </Box>
                      </When>

                      <StyledMapWrapperContainer
                        onClick={() => {
                          handleMapClick(values, setSubmitting, errors);
                        }}
                        showConfirmBanner={
                          !isAllRequiredFieldsNotAdded(values, errors) &&
                          !values?.user_latitude &&
                          !values?.user_longitude
                        }
                      >
                        {MemoizedStaticMap(setFieldValue, values, touched)}
                        <StyledTextOverLayContainer>{t('message.tapMapToEditYourPin')}</StyledTextOverLayContainer>
                      </StyledMapWrapperContainer>
                    </When>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isDefault"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultChecked={Boolean(userAddressList?.length === 0 ? true : address?.isDefault)}
                        value={userAddressList?.length === 0 ? true : values.isDefault}
                        checked={userAddressList?.length === 0 ? true : values.isDefault}
                        disabled={Boolean(userAddressList?.length === 0 ? true : address?.isDefault)}
                        sx={{'&.Mui-disabled': {color: theme.palette.primary.light}}}
                      />
                    }
                    label={t('label.setDefaultAddress')}
                  />
                </Grid>
                <Grid xs={12} item spacing={2}>
                  <Button
                    fullWidth
                    type="submit"
                    shape="rounded"
                    variant="contained"
                    disabled={isLoading || isSubmitting || isAllRequiredFieldsNotAdded(values, errors)}
                    sx={{height: theme.spacing(6), px: 4}}
                    id="btn_save"
                    loading={isLoading || loading}
                  >
                    {t('button.save')}
                  </Button>
                </Grid>
              </Grid>
            </When>
            <When condition={fullMapView}>
              <AddressFullMapView
                addressString={isMapStringUpdated(values, touched) ? getAddressStringWrapper(values, true) : undefined}
                latLng={
                  !isMapStringUpdated(values, touched)
                    ? {
                        lat: values?.user_latitude || values?.latitude,
                        lng: values?.user_longitude || values?.longitude
                      }
                    : undefined
                }
                setFullMapView={setFullMapView}
                filteredStoreArea={filteredStoreArea}
                setOpenFullMapView={setOpenFullMapView}
                addAddressEvent={addAddressEvent}
              />
            </When>
            <When condition={isContactFormVisible}>
              <ContactInformationModal
                open={isContactFormVisible}
                onClose={() => setIsContactFormVisible(false)}
                onSave={(newContactInfo: Partial<IUserDetail>) => {
                  _updateContactInformation(values, newContactInfo);
                  setIsContactFormVisible(false);
                }}
                values={values}
              />
            </When>
          </Form>
        )}
      </Formik>
    </>
  );
};

const SelectMenuProps: Partial<MenuProps> = {
  style: {
    maxHeight: '50%'
  }
};
